// assets/pagination-load-more.js
(() => {
  const SELECTOR = '[data-pagination][data-load-more]';
  const STORAGE_PREFIX = 'gwa:load-more:';
  const RESTORE_MAX_AGE = 30 * 60 * 1000;

  function qs(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  function getString(wrapper, name, fallback) {
    return wrapper?.getAttribute(name) || fallback;
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function getStateKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.hash = '';
    return STORAGE_PREFIX + url.pathname + '?' + url.searchParams.toString();
  }

  function isBackForwardNavigation() {
    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    return nav ? nav.type === 'back_forward' : performance.navigation && performance.navigation.type === 2;
  }

  function isReturnFromRememberedProduct(state) {
    if (!state || !state.productPath || !document.referrer) return false;

    try {
      return new URL(document.referrer).pathname === state.productPath;
    } catch (e) {
      return false;
    }
  }

  function shouldRestoreState(state) {
    return isBackForwardNavigation() || isReturnFromRememberedProduct(state);
  }

  function readState() {
    try {
      const raw = sessionStorage.getItem(getStateKey());
      if (!raw) return null;

      const state = JSON.parse(raw);
      if (!state || !Array.isArray(state.urls)) return null;
      if (Date.now() - Number(state.updatedAt || 0) > RESTORE_MAX_AGE) return null;

      return state;
    } catch (e) {
      return null;
    }
  }

  function writeState(patch) {
    try {
      const current = readState() || { urls: [] };
      const next = {
        ...current,
        ...patch,
        updatedAt: Date.now()
      };
      sessionStorage.setItem(getStateKey(), JSON.stringify(next));
    } catch (e) {}
  }

  function rememberLoadedUrl(url) {
    const state = readState() || { urls: [] };
    const urls = state.urls.includes(url) ? state.urls : [...state.urls, url];
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    writeState({ urls, scrollY: window.scrollY || 0 });
  }

  function setManualScrollRestoration() {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }

  function rememberScroll() {
    writeState({ scrollY: window.scrollY || 0 });
  }

  function rememberProductClick(link) {
    if (!link) return;

    const href = link.getAttribute('href') || '';
    const productPath = href ? new URL(href, window.location.href).pathname : '';

    writeState({
      productPath,
      scrollY: window.scrollY || 0
    });
  }

  function restoreScrollPosition(state) {
    const scrollY = Number(state && state.scrollY ? state.scrollY : 0);
    if (!scrollY) return;

    // Robust: wiederholt versuchen, bis die Zielposition erreicht ODER die Seite
    // hoch genug ist. Beim Zurueck-Laden ist das Grid (inkl. Bilder) anfangs noch
    // kurz -> ein einmaliges scrollTo wuerde auf die kleine Hoehe geklemmt und
    // landete oben. Bricht ab, sobald der Nutzer selbst scrollt/interagiert.
    const tolerance = 4;
    const maxWait = 3000;
    const start = Date.now();
    let iv = null;

    const maxScrollable = () => document.documentElement.scrollHeight - window.innerHeight;

    const stop = () => {
      if (iv) clearInterval(iv);
      iv = null;
      window.removeEventListener('wheel', onUserInput);
      window.removeEventListener('touchstart', onUserInput);
      window.removeEventListener('keydown', onUserInput);
    };

    const onUserInput = () => stop();

    const tick = () => {
      // Erst scrollen, wenn die Seite die Zielposition ueberhaupt hergibt.
      if (maxScrollable() >= scrollY - tolerance) {
        window.scrollTo(0, scrollY);
      }
      if (Math.abs(window.scrollY - scrollY) <= tolerance || Date.now() - start > maxWait) {
        stop();
      }
    };

    window.addEventListener('wheel', onUserInput, { passive: true });
    window.addEventListener('touchstart', onUserInput, { passive: true });
    window.addEventListener('keydown', onUserInput);

    iv = setInterval(tick, 50);
    tick();
  }

  function hideButton(btn) {
    if (!btn) return;

    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.hidden = true;
    btn.style.display = 'none';
  }

  function showButton(btn) {
    if (!btn) return;

    btn.disabled = false;
    btn.removeAttribute('aria-disabled');
    btn.hidden = false;
    btn.style.display = '';
  }

  /**
   * Merge current page URL params (filters, sort_by, etc.) with the
   * page number taken from nextUrl.
   */
  function buildFetchUrl(nextUrl) {
    const next = new URL(nextUrl, window.location.href);
    const merged = new URL(window.location.href);

    const page = next.searchParams.get('page');

    if (page) {
      merged.searchParams.set('page', page);
    } else {
      merged.searchParams.delete('page');
    }

    merged.hash = '';

    return merged.toString();
  }

  async function appendPage(wrapper, fetchUrl) {
    const containerSelector = wrapper.getAttribute('data-container-selector');
    const itemSelector = wrapper.getAttribute('data-item-selector') || null;
    const container = document.querySelector(containerSelector);

    if (!container) {
      console.warn('[LoadMore] container not found:', containerSelector);
      return null;
    }

    const res = await fetch(fetchUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!res.ok) throw new Error('HTTP ' + res.status);

    const html = await res.text();
    const doc = parseHTML(html);
    const newContainer = doc.querySelector(containerSelector);

    if (!newContainer) throw new Error('Container not found in response');

    const nodes = itemSelector
      ? Array.from(newContainer.querySelectorAll(itemSelector))
      : Array.from(newContainer.children);

    if (!nodes.length) return { nodes: 0, nextUrl: null };

    nodes.forEach((n) => container.appendChild(n));

    const newWrapper = doc.querySelector(SELECTOR);
    const newNext = newWrapper ? newWrapper.getAttribute('data-next-url') : null;

    return { nodes: nodes.length, nextUrl: newNext };
  }

  function dispatchLoaded(containerSelector, nodes) {
    document.dispatchEvent(new CustomEvent('pagination:loaded', {
      detail: {
        containerSelector,
        nodes
      }
    }));

    document.dispatchEvent(new CustomEvent('swym:collections-loaded', {
      detail: {
        containerSelector,
        nodes
      }
    }));
  }

  function updateNextUrl(wrapper, btn, status, nextUrl) {
    if (nextUrl) {
      wrapper.setAttribute('data-next-url', nextUrl);
      showButton(btn);

      if (status) status.textContent = '';
    } else {
      wrapper.removeAttribute('data-next-url');
      hideButton(btn);

      if (status) status.textContent = '';
    }
  }

  async function loadMore(wrapper) {
    const btn = qs(wrapper, '[data-load-more-btn]');
    const status = qs(wrapper, '[data-load-more-status]');
    const containerSelector = wrapper.getAttribute('data-container-selector');
    const anchor = wrapper.getAttribute('data-anchor') || '';
    const nextUrl = wrapper.getAttribute('data-next-url');

    if (!nextUrl || !containerSelector) {
      hideButton(btn);
      return;
    }

    try {
      btn && (btn.disabled = true);

      if (status) status.textContent = getString(wrapper, 'data-loading-label', 'Loading...');

      const fetchUrl = buildFetchUrl(nextUrl) + anchor;
      const result = await appendPage(wrapper, fetchUrl);

      if (!result || !result.nodes) {
        wrapper.removeAttribute('data-next-url');
        if (status) status.textContent = '';
        hideButton(btn);
        return;
      }

      rememberLoadedUrl(fetchUrl);
      updateNextUrl(wrapper, btn, status, result.nextUrl);
      dispatchLoaded(containerSelector, result.nodes);
    } catch (e) {
      console.error('[LoadMore] error:', e);

      if (wrapper.getAttribute('data-next-url')) {
        showButton(btn);
      }

      if (status) status.textContent = getString(wrapper, 'data-error-label', 'Failed to load. Please try again.');
    }
  }

  async function restoreLoadedPages(wrapper) {
    const state = readState();
    if (wrapper.dataset.loadMoreRestored === 'true' || !state || !shouldRestoreState(state)) return;

    setManualScrollRestoration();

    wrapper.dataset.loadMoreRestored = 'true';

    const btn = qs(wrapper, '[data-load-more-btn]');
    const status = qs(wrapper, '[data-load-more-status]');

    try {
      if (btn) btn.disabled = true;
      if (status && state.urls.length) status.textContent = getString(wrapper, 'data-loading-label', 'Loading...');

      let nextUrl = wrapper.getAttribute('data-next-url');
      for (const url of state.urls) {
        const result = await appendPage(wrapper, url);
        if (result) nextUrl = result.nextUrl;
      }

      if (state.urls.length) {
        updateNextUrl(wrapper, btn, status, nextUrl);
        dispatchLoaded(wrapper.getAttribute('data-container-selector'), state.urls.length);
      } else if (status) {
        status.textContent = '';
      }

      restoreScrollPosition(state);
    } catch (e) {
      console.error('[LoadMore] restore error:', e);
      if (wrapper.getAttribute('data-next-url')) showButton(btn);
      if (status) status.textContent = '';
      restoreScrollPosition(state);
    }
  }

  function init(wrapper) {
    const btn = qs(wrapper, '[data-load-more-btn]');

    if (!btn || wrapper.dataset.loadMoreInitialized === 'true') return;

    wrapper.dataset.loadMoreInitialized = 'true';
    setManualScrollRestoration();

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      loadMore(wrapper);
    });

    if (!wrapper.getAttribute('data-next-url')) {
      hideButton(btn);
    } else {
      showButton(btn);
    }

    const containerSelector = wrapper.getAttribute('data-container-selector');
    const container = containerSelector ? document.querySelector(containerSelector) : null;
    if (container && !container.dataset.loadMoreStateBound) {
      container.dataset.loadMoreStateBound = 'true';
      container.addEventListener('click', (event) => {
        const link = event.target.closest('a[href*="/products/"]');
        if (link) rememberProductClick(link);
      });
    }

    restoreLoadedPages(wrapper);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SELECTOR).forEach(init);
  });

  window.addEventListener('pagehide', rememberScroll);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') rememberScroll();
  });

  // Mobile browsers often keep the collection in their back/forward cache.
  // Its product cards are already present in that case, so only restore the
  // saved offset and never append the stored pages a second time.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    const state = readState();
    if (state) restoreScrollPosition(state);
  });

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target;

    if (!section) return;

    section.querySelectorAll(SELECTOR).forEach(init);
  });

  document.addEventListener('facets:updated', () => {
    document.querySelectorAll(SELECTOR).forEach(init);
  });
})();
